import api from "@utils/api.client"
import { create } from "zustand"
import { devtools } from "zustand/middleware"

export interface Application {
  id: string
  apartmentAddress?: string
  proposeApartment: boolean
  moveInDate: string
  desiredSize: string[]
  numberOfOccupants: number
  quebecResidenceDuration: "plus d'un an" | "moins d'un an"
  comfortableWithCreditCheck: boolean
  hasGuarantor: boolean
  animals: string[]
  visitAvailability: string[]
  firstName: string
  lastName: string
  email: string
  phone: string
  additionalSpecifications?: string
  submittedAt: string
  status: string
}

interface ApplicationStore {
  applications: Application[]
  loading: boolean
  error: string | null
  fetchAll: () => Promise<void>
  addApplication: (application: Omit<Application, "id" | "submittedAt">) => Promise<void>
  updateApplication: (id: string, partialData: Partial<Application>) => Promise<void>
  removeApplication: (id: string) => Promise<void>
}

export const useApplicationStore = create<ApplicationStore>()(
  devtools((set, get) => ({
    applications: [],
    loading: false,
    error: null,
    fetchAll: async () => {
      set({ loading: true, error: null })
      try {
        const response = await api.get<Application[]>("applications-for-rent")
        set({ applications: response.data, loading: false })
      } catch (error: any) {
        set({ error: error.message, loading: false })
      }
    },
    addApplication: async (application) => {
      set({ loading: true, error: null })
      try {
        const newApplication = await api.post<Application>("applications-for-rent", application)
        set((state) => ({
          applications: [...state.applications, newApplication.data],
          loading: false,
        }))
      } catch (error: any) {
        set({ error: error.message, loading: false })
      }
    },

    updateApplication: async (id, partialData) => {
      set({ loading: true, error: null })
      try {
        // 1) Grab the current item from the store
        const existing = get().applications.find((a) => a.id === id)

        if (!existing) {
          throw new Error(`No application with id="${id}" found.`)
        }

        // 2) Merge partial data with the existing item
        const merged = { ...existing, ...partialData }
        Reflect.deleteProperty(merged, "id")

        // 3) Send the *full* merged object to your server
        //    (assuming your server endpoint expects a full object).
        //    If your backend does partial merges via a PATCH route, do that here.
        const { data: updatedApp } = await api.post<any>(`applications-for-rent/${id}`, merged)

        // 4) Replace that item in state with the server’s response
        set((state) => ({
          applications: state.applications.map((app) => (app.id === id ? updatedApp : app)),
          loading: false,
        }))
      } catch (error: any) {
        set({ error: error.message, loading: false })
      }
    },

    removeApplication: async (id) => {
      set({ loading: true, error: null })
      try {
        await api.post<Application>(`applications-for-rent/${id}/delete`)
        set((state) => ({
          applications: state.applications.filter((app) => app.id !== id),
          loading: false,
        }))
      } catch (error: any) {
        set({ error: error.message, loading: false })
      }
    },
  })),
)
